import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Download, Eye } from 'react-feather';
import { UncontrolledTooltip } from 'reactstrap';

import useI18n from 'hooks/use-i18n';
import { bytesToSize, formatDuration } from 'helpers/number';
import { downloadFile, handleToastError } from 'helpers/index';
import Router from 'next/router';
import styles from 'styles/Globals.module.scss';
import { formatHHmmDDMMYYYY } from '../../../helpers/format-time';
import fileApi from 'api/fileApi';
import { DOMAIN_HOST } from 'config';
import { FILE_ACCESS } from 'modules/Files/constants';
import { usePermissions } from 'hooks/permission/use-has-permission';
import { useUnit } from 'hooks/units';
import { useViewMode } from 'hooks';
import { API_BASE_URL } from 'config';

const RecordedItem = ({ record, index, order, siblingRecordIndex }) => {
  const i18n = useI18n();
  const unitSelected = useUnit();
  const viewMode = useViewMode();
  const [permissions] = usePermissions(FILE_ACCESS);

  const downloadUrl = useMemo(
    () => `${API_BASE_URL}/files/file-download?id=${getLink()}`,
    [record.id],
  );

  const hasAccessRole = useCallback(() => {
    const settings = unitSelected?.config?.liveClassSettings;
    const mode = viewMode?.toLowerCase();
    if (
      settings &&
      Object.keys(settings).length > 0 &&
      (viewMode === 'INSTRUCTOR' || viewMode === 'LEARNER')
    ) {
      return {
        view: settings[mode]?.view,
        download: settings[mode]?.download,
      };
    }

    return {
      view: viewMode === 'ADMIN' || false,
      download: viewMode === 'ADMIN' || false,
    };
  }, [unitSelected?.config?.liveClassSettings, viewMode]);

  const handleStartRecord = async () => {
    try {
      const linkUrl = getLink();

      if (linkUrl) {
        window.open(DOMAIN_HOST + `video-player/${linkUrl}`, '_blank');
        return null;
      }

      const url = record.play_url;
      if (url) {
        window.open(url, '_blank');
      }
    } catch (e) {
      handleToastError(e, i18n);
    }
  };

  function getLink() {
    try {
      let url = '';
      if (record?.localFile) {
        url = record.localFile;
      }
      return url;
    } catch (error) {
      throw error;
    }
  }

  return (
    <tr>
      <td className="text-left">{index + 1}</td>
      <td className="text-left text-nowrap">
        {record?.play_url &&
        hasAccessRole().view &&
        permissions.get('RECORDINGS_VIEW')?.allowed ? (
          <a
            onClick={handleStartRecord}
            target="_blank"
            rel="noreferrer"
            className="text-underline"
            alt={record.play_url}
          >
            Video recorded P.
            {order}
            {siblingRecordIndex > 0 ? `.${siblingRecordIndex}` : null}
          </a>
        ) : (
          '--'
        )}
      </td>
      <td className="text-left">{bytesToSize(record?.file_size || 0)}</td>
      <td className="text-left">
        {record?.recording_start
          ? formatHHmmDDMMYYYY(record.recording_start)
          : '--'}
      </td>
      <td style={{ paddingLeft: '40px' }}>
        {permissions.get('RECORDINGS_VIEW')?.allowed && hasAccessRole().view && (
          <>
            <span
              className="cursor-pointer"
              id="viewRecord"
              onClick={handleStartRecord}
            >
              <Eye size={16} />
            </span>
            <UncontrolledTooltip target="viewRecord">
              View record
            </UncontrolledTooltip>
          </>
        )}

        {permissions.get('DOWNLOAD_URL')?.allowed &&
          getLink() &&
          hasAccessRole().download && (
            <a href={downloadUrl} download className="ml-05">
              <span className="cursor-pointer" id="downloadRecoard">
                <Download size={16} color="#646871" />
              </span>
              <UncontrolledTooltip target="downloadRecoard">
                Download
              </UncontrolledTooltip>
            </a>
          )}
        {!permissions.get('RECORDINGS_VIEW')?.allowed &&
          !permissions.get('DOWNLOAD_URL')?.allowed &&
          !hasAccessRole().download &&
          '--'}
      </td>
    </tr>
  );
};

RecordedItem.propTypes = {
  record: PropTypes.object.isRequired,
};

export default RecordedItem;
